import React, { useMemo } from 'react'
import { flattenTree } from '../../utils/treeUtils'
import { useDebounce } from '../../../utils/hooks'
import { FilterPanel } from './FilterPanel'
import CheckboxTree, { StringTitleTreeNode } from './CheckboxTree'

interface TreeCheckboxFilterProps {
  treeData: StringTitleTreeNode[]
  value?: string[]
  onChange?: (values: string[]) => void
  checkOnSearch?: boolean
  placeholder?: string
  width?: number | string
}

/** Collect keys of nodes whose title matches the search */
const getMatchingKeys = (nodes: StringTitleTreeNode[], search: string): string[] => {
  const lower = search.toLowerCase()
  const keys: string[] = []
  const traverse = (items: StringTitleTreeNode[]) => {
    for (const node of items) {
      if (node.title.toLowerCase().includes(lower)) {
        keys.push(String(node.key))
      }
      if (node.children) traverse(node.children)
    }
  }
  traverse(nodes)
  return keys
}

/** Convenience wrapper: FilterPanel + CheckboxTree */
const TreeCheckboxFilter: React.FC<TreeCheckboxFilterProps> = ({
  treeData,
  value = [],
  onChange,
  checkOnSearch = false,
  placeholder,
  width
}) => {
  const [searchText, setSearchText] = React.useState('')
  const debouncedSearchText = useDebounce(searchText, 300)

  const allKeys = useMemo(() => flattenTree(treeData, { valueField: 'key', childrenField: 'children' }), [treeData])

  const handleSearch = () => {
    // Use debouncedSearchText so the selection matches what is currently
    // visible in the tree (which is filtered by debouncedSearchText).
    const query = debouncedSearchText?.trim()
    if (checkOnSearch && query) {
      const matchingKeys = getMatchingKeys(treeData, query)
      onChange?.(Array.from(new Set([...value, ...matchingKeys])))
    }
  }

  return (
    <FilterPanel
      searchText={searchText}
      onSearchChange={setSearchText}
      onSearch={handleSearch}
      selectedCount={value.length}
      totalCount={allKeys.length}
      onClear={() => onChange?.([])}
      showEnterHint={checkOnSearch}
      placeholder={placeholder}
      {...(width !== undefined && { width })}
    >
      <CheckboxTree treeData={treeData} value={value} onChange={onChange} searchText={debouncedSearchText} />
    </FilterPanel>
  )
}

export default TreeCheckboxFilter
